import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { toast } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import TelegramVerifyPanel, { VerifyPayload } from '../components/TelegramVerifyPanel';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // If an unverified operator tries to log in, the backend returns 403 with a
  // fresh verification payload — show the verify panel instead of an error.
  const [verifyPayload, setVerifyPayload] = useState<VerifyPayload | null>(null);
  const { login, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Предотвращаем множественные отправки
    if (isLoading) {
      return;
    }

    try {
      await login(email, password);
      toast.success('Успешный вход в систему!');
      navigate('/dashboard');
    } catch (error: any) {
      const data = error.response?.data?.data;
      if (data?.requiresTelegramVerification) {
        setVerifyPayload(data as VerifyPayload);
        return;
      }
      // Сначала — человеческое сообщение от бэкенда (напр. «Неверный email или пароль»),
      // потом нормализованное сетевое, и лишь затем общий фолбэк.
      const errorMessage =
        error.response?.data?.message ||
        (error.response?.status === 429
          ? 'Слишком много попыток входа. Подождите немного.'
          : error.message) ||
        'Ошибка входа. Проверьте данные.';
      toast.error(errorMessage);
    }
  };

  if (verifyPayload) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6 bg-card border border-border rounded-lg p-8 shadow">
          <TelegramVerifyPanel
            payload={verifyPayload}
            onVerified={async () => {
              setVerifyPayload(null);
              try {
                await login(email, password);
                toast.success('Успешный вход в систему!');
                navigate('/dashboard');
              } catch (e: any) {
                // Verified but still inactive → backend explains (admin must activate).
                toast.success(e?.response?.data?.message || 'Аккаунт подтверждён. Войдите ещё раз.', { duration: 8000 });
              }
            }}
          />
          <div className="text-center">
            <button
              type="button"
              onClick={() => setVerifyPayload(null)}
              className="text-sm text-primary hover:text-primary/80"
            >
              Назад ко входу
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary/10">
            <svg className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
            Вход в GGDesk CRM
          </h2>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            Система управления заявками
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground">
                Email
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1"
                placeholder="Введите email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground">
                Пароль
              </label>
              <div className="mt-1 relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="pr-10"
                  placeholder="Введите пароль"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute inset-y-0 right-0 pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          <div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? 'Вход...' : 'Войти'}
            </Button>
          </div>

          <div className="text-center">
            <Link 
              to="/register" 
              className="text-sm text-primary hover:text-primary/80"
            >
              Нет аккаунта? Зарегистрироваться
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;